import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Button,
  Skeleton,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import axios from "../../helpers/request";
import moment from "moment";
import RoomLogs from "../RoomLogs/RoomLogs";
import logo from "../images/logo.svg";

// Main container
const MainContainer = styled.main({
  flexGrow: 1,
  minHeight: "100vh",
  overflow: "auto",
  backgroundColor: "#f8fafc",
  padding: 0,
});

// Header bar
const HeaderBar = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 24px",
  backgroundColor: "#fff",
  borderBottom: "1px solid #e2e8f0",
});

const HeaderLeft = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const BackButton = styled.span({
  fontSize: "14px",
  fontWeight: 500,
  color: "#10416D",
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
});

const LogoImage = styled.img({
  height: "28px",
});

const BrandName = styled.span({
  fontSize: "18px",
  fontWeight: 600,
  color: "#10416D",
  letterSpacing: "-0.5px",
});

const HeaderRight = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const HeaderTitle = styled.span({
  fontSize: "13px",
  fontWeight: 500,
  color: "#10416D",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

// Content wrapper
const ContentWrapper = styled.div({
  padding: "24px",
});

// Overview card
const OverviewCard = styled(Paper)({
  backgroundColor: "#fff",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  padding: "24px",
  marginBottom: "24px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const SectionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "#10416D",
  marginBottom: "20px",
});

const DetailsGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "24px",
  "@media (max-width: 960px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
  },
});

const DetailColumn = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const DetailRow = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const DetailLabel = styled.span({
  fontSize: "11px",
  fontWeight: 500,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const DetailValue = styled.span({
  fontSize: "14px",
  fontWeight: 500,
  color: "#334155",
});

const Badge = styled.span(({ variant }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 10px",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 500,
  backgroundColor: variant === "yes" ? "#dcfce7" : "#fee2e2",
  color: variant === "yes" ? "#16a34a" : "#dc2626",
}));

const StatusBadge = styled.span(({ status }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 10px",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 500,
  backgroundColor: status === "completed" ? "#dcfce7" : "#fef3c7",
  color: status === "completed" ? "#16a34a" : "#b45309",
}));

export default function RoomLogsPage() {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participantCount, setParticipantCount] = useState(0);
  const [roomStatus, setRoomStatus] = useState(null);

  useEffect(() => {
    if (meetingId) {
      fetchMeetingDetails();
      fetchParticipants();
    } else {
      navigate("/", { replace: true });
    }
  }, [meetingId]);

  const fetchMeetingDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/meeting/" + meetingId);
      if (response && response.status === 200 && response.data) {
        setMeeting(response.data);

        // Get room status - only if room_id exists
        if (response.data.room_id) {
          const statusResponse = await axios.get(
            `/internal/get-room-status/${response.data.room_id}`
          );
          setRoomStatus(statusResponse?.data?.status || "unknown");
        } else {
          setRoomStatus("unknown");
        }
      }
    } catch (error) {
      console.error("Error fetching meeting details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(
        "/internal/meeting/" + meetingId + "/participant"
      );
      if (response && response.status === 200) {
        setParticipantCount(response.data.length);
      }
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  };

  const formatDuration = () => {
    if (!meeting?.ended_at || !meeting?.started_at) return "-";
    const endTime = meeting.meeting_ended_at || meeting.ended_at;
    const duration = Math.ceil(
      moment(endTime).diff(moment(meeting.started_at), "minutes", true)
    );
    return `${duration} minute(s)`;
  };

  if (loading) {
    return (
      <MainContainer>
        <HeaderBar>
          <HeaderLeft>
            <Link to={`/interaction/${meetingId}`} style={{ textDecoration: "none" }}>
              <BackButton>← Back</BackButton>
            </Link>
            <LogoImage src={logo} alt="Dialectica" />
          </HeaderLeft>
          <HeaderRight>
            <HeaderTitle>ROOM ACTIVITY LOGS</HeaderTitle>
          </HeaderRight>
        </HeaderBar>
        <ContentWrapper>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </ContentWrapper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      {/* Header Bar */}
      <HeaderBar>
        <HeaderLeft>
          <Link to={`/interaction/${meetingId}`} style={{ textDecoration: "none" }}>
            <BackButton>← Back</BackButton>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LogoImage src={logo} alt="Dialectica" />
          </div>
        </HeaderLeft>
        <HeaderRight>
          <HeaderTitle>ROOM ACTIVITY LOGS</HeaderTitle>
        </HeaderRight>
      </HeaderBar>

      <ContentWrapper>
        {/* Room Details Card */}
        <OverviewCard elevation={0}>
          <SectionTitle>Room Details</SectionTitle>

          <DetailsGrid>
            {/* Column 1 */}
            <DetailColumn>
              <DetailRow>
                <DetailLabel>TWILIO ID</DetailLabel>
                <DetailValue>{meeting?.meeting_id || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>TOPIC</DetailLabel>
                <DetailValue>{meeting?.topic || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>MAVEN ID</DetailLabel>
                <DetailValue>{meeting?.maven_id || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>MAVEN SOURCE</DetailLabel>
                <DetailValue>{meeting?.source || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>PARTICIPANTS</DetailLabel>
                <DetailValue>{participantCount}</DetailValue>
              </DetailRow>
            </DetailColumn>

            {/* Column 2 */}
            <DetailColumn>
              <DetailRow>
                <DetailLabel>MEETING DATE</DetailLabel>
                <DetailValue>
                  {meeting?.datetime ? moment(meeting.datetime).format("DD/MM/YYYY") : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>START TIME (UTC)</DetailLabel>
                <DetailValue>
                  {meeting?.started_at ? moment(meeting.started_at).format("HH:mm") : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>END TIME (UTC)</DetailLabel>
                <DetailValue>
                  {meeting?.ended_at ? moment(meeting.ended_at).format("HH:mm") : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>DURATION</DetailLabel>
                <DetailValue>{formatDuration()}</DetailValue>
              </DetailRow>
            </DetailColumn>

            {/* Column 3 */}
            <DetailColumn>
              <DetailRow>
                <DetailLabel>RECORDING</DetailLabel>
                <DetailValue>{meeting?.record_meeting ? "Yes" : "No"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>ANONYMOUS</DetailLabel>
                <DetailValue>{meeting?.is_anonymous ? "Yes" : "No"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>DISCLAIMER</DetailLabel>
                <DetailValue>{meeting?.show_disclaimer ? "Yes" : "No"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>STATUS</DetailLabel>
                <DetailValue>
                  <StatusBadge status={roomStatus}>
                    {roomStatus === "completed" ? "Completed" : "In Progress"}
                  </StatusBadge>
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>ARCHIVED / DELETED</DetailLabel>
                <DetailValue style={{ display: "flex", gap: "8px" }}>
                  <Badge variant={meeting?.is_archived ? "yes" : "no"}>
                    {meeting?.is_archived ? "Yes" : "No"}
                  </Badge>
                  <Badge variant={meeting?.is_deleted ? "yes" : "no"}>
                    {meeting?.is_deleted ? "Yes" : "No"}
                  </Badge>
                </DetailValue>
              </DetailRow>
            </DetailColumn>
          </DetailsGrid>
        </OverviewCard>

        {/* Room Activity Logs Component */}
        <RoomLogs meetingId={meetingId} roomSid={meeting?.room_id} />
      </ContentWrapper>
    </MainContainer>
  );
}

